<template>
  <div
    :class="[
      $style.inline,
      isFullWidth && $style.fullWidthWrapper
    ]"
    @click="isDisabled || $emit('click')"
  >
    <div
      :class="[
        'isButton',
        $style.wrapper,
        $style[`variant-${variant}`],
        $style[`font-weight-${fontWeight}`],
        $style[`font-size-${fontSize}`],
        $style[`align-${alignment}`],
        $style[`size-${size}`],
        textColor && $style[`text-color-${textColor}`],
        isRounded && $style.rounded,
        leftRounded && $style.leftRounded,
        rightRounded && $style.rightRounded,
        isDisabled && $style.disabled,
        className
      ]"
    >
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isRounded: {
      type: Boolean,
      required: false,
      default: false,
    },
    variant: {
      type: String,
      default: 'orange',
      required: false,
    },
    fontWeight: {
      type: Number,
      default: 400,
      required: false,
    },
    size: {
      type: String,
      default: 'md',
      required: false,
    },
    fontSize: {
      type: String,
      default: 'normal',
      required: false,
    },
    isFullWidth: {
      type: Boolean,
      default: false,
      required: false,
    },
    leftRounded: {
      type: Boolean,
      default: false,
      required: false,
    },
    rightRounded: {
      type: Boolean,
      default: false,
      required: false,
    },
    alignment: {
      type: String,
      default: 'center',
      required: false,
    },
    textColor: {
      type: String,
      default: '',
      required: false,
    },
    className: {
      type: String,
      default: '',
      required: false,
    },
  },
}
</script>

<style module lang="scss">
  @import "../../assets/scss/mixins";
  @import "../../assets/scss/variables";

  .inline {
    display: inline;
    pointer-events: all;
  }
  .fullWidthWrapper {
    width: 100%;
  }
  .wrapper {
    display: flex;
    align-items: center;
    user-select: none;
    cursor: pointer;
    color: $color-white;
    padding: .40rem;
    transition: all .3s ease-in-out;

    &.align-center { justify-content: center; text-align: center; }
    &.align-left { justify-content: flex-start; }

    &.size-sm { padding: .2rem }
    &.size-md { padding: .4rem }
    &.size-xl { padding: .7rem 2rem }

    &.font-weight-200 { font-weight: 200; }
    &.font-weight-400 { font-weight: 400; }
    &.font-weight-600 { font-weight: 600; }

    &.font-size-xlarge { font-size: $font-size-xlarge; }
    &.font-size-normal { font-size: $font-size-normal; }
    &.font-size-small { font-size: $font-size-small; }
    &.font-size-tiny { font-size: $font-size-tiny; }

    &.rounded { border-radius: 20px; }
    &.leftRounded { border-radius: 20px 0 0 20px; }
    &.rightRounded { border-radius: 0 20px 20px 0; }

    &.variant-orange {
      border: 2px solid $color-tomato;
      @include linear-gradient($color-benihi-red, $color-tomato);
      &:hover {
        @include linear-gradient(darken($color-benihi-red, 5%), darken($color-tomato, 5%));
      }
    }
    &.variant-outline-orange {
      color: $color-tomato;
      border: 2px solid $color-tomato;
      &:hover {
        box-shadow: 0 0 3px 1px $color-tomato;
      }
    }
    &.variant-outline-light {
      color: $color-white;
      border: 2px solid $color-white;
      &:hover {
        box-shadow: 0 0 3px 1px $color-white;
      }
    }

    &.variant-blue {
      border: 2px solid $color-bleu-de-france;
      @include linear-gradient(darken($color-bleu-de-france, 2%), $color-bleu-de-france);
      &:hover {
        @include linear-gradient(
            darken($color-bleu-de-france, 4%),
            darken($color-bleu-de-france, 2%)
        );
      }
    }

    &.variant-outline-blue {
      color: $color-bleu-de-france;
      border: 2px solid $color-bleu-de-france;
      &:hover {
        box-shadow: 0 0 3px 1px $color-bleu-de-france;
      }
    }

    &.variant-green {
      border: 2px solid $color-bubble-bobble-green;
      @include linear-gradient(darken($color-bubble-bobble-green, 2%), $color-bubble-bobble-green);
      &:hover {
        @include linear-gradient(
            darken($color-bubble-bobble-green, 4%),
            darken($color-bubble-bobble-green, 2%)
        );
      }
    }
    &.variant-outline-green {
      color: $color-bubble-bobble-green;
      border: 2px solid $color-bubble-bobble-green;
      &:hover {
        box-shadow: 0 0 3px 1px $color-bubble-bobble-green;
      }
    }

    &.variant-light {
      background-color: $color-alley;
      color: $color-midnight-blue;
      &:hover {
        background-color: darken($color-alley, 5%);
      }
    }

    &.variant-gray {
      background-color: $color-bleached-silk;
      color: $color-carbon;
      &:hover {
        background-color: darken($color-bleached-silk, 5%);
      }
    }

    &.variant-dark {
      color: $color-yin-bai-silver;
      background-color: $color-tristesse;
      &:hover {
        background-color: darken($color-tristesse, 5%);
      }
    }

    &.disabled {
      pointer-events: none;
      opacity: .8;
    }

    &.text-color-white {
      color: $color-white;
    }
  }
</style>
